import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Menu, X, Building2, Mail, Users, LayoutDashboard, BookOpen,
  UserCircle, Play, Smartphone, ChevronDown, Eye, Map,
  BarChart3, FileCode, ArrowRight, CreditCard, Phone,
  Sparkles, Globe
} from 'lucide-react';
import brandConfig from '../../config/brandConfig';

// ─── Mega-menu content ───────────────────────────────────────────────
const PRODUCT_ITEMS = [
  {
    section: 'Plataforma',
    items: [
      { href: `${brandConfig.dashboardUrl}/iniciar-sesion`, icon: <LayoutDashboard className="w-5 h-5" />, label: 'Dashboard', desc: 'Panel de control para gestionar lotes y ventas', gradient: 'from-orange-500 to-amber-500', external: false },
      { href: '/app-movil', icon: <Smartphone className="w-5 h-5" />, label: 'App Movil', desc: 'Tus lotes en manos de tus clientes', gradient: 'from-emerald-500 to-teal-500', external: false },
      { href: brandConfig.projectDemoUrl, icon: <Eye className="w-5 h-5" />, label: 'Visor Interactivo', desc: 'Mapas con detalle de cada lote en tiempo real', gradient: 'from-blue-500 to-cyan-500', external: true },
    ]
  },
  {
    section: 'Desarrolladores',
    items: [
      { href: '/documentacion', icon: <BookOpen className="w-5 h-5" />, label: 'API Docs', desc: 'Integracion REST para tu sistema', gradient: 'from-violet-500 to-purple-500', external: false },
      { href: '/documentacion-geojson', icon: <FileCode className="w-5 h-5" />, label: 'GeoJSON Docs', desc: 'Especificacion de formato geoespacial', gradient: 'from-pink-500 to-rose-500', external: false },
      { href: '/convertir-geojson', icon: <Map className="w-5 h-5" />, label: 'Convertir GeoJSON', desc: 'Herramienta de conversion online', gradient: 'from-indigo-500 to-blue-500', external: false },
    ]
  }
];

const SOLUTIONS_ITEMS = [
  { href: '/como-funciona/inmobiliarias', icon: <Building2 className="w-5 h-5" />, label: 'Para Inmobiliarias', desc: 'Publica, gestiona y vende tus lotes con herramientas profesionales', gradient: 'from-orange-500 to-amber-500' },
  { href: '/como-funciona/clientes', icon: <UserCircle className="w-5 h-5" />, label: 'Para Compradores', desc: 'Encuentra tu lote ideal con busqueda inteligente y alertas', gradient: 'from-blue-500 to-cyan-500' },
];

// ─── Dropdown wrapper (click-based, closes on outside click & Escape) ─
const Dropdown = ({ label, children, wide }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) close(); };
    const handleKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('mousedown', handleClick); document.removeEventListener('keydown', handleKey); };
  }, [open, close]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${
          open
            ? 'text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800'
            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800/60'
        }`}
        aria-expanded={open}
      >
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Panel */}
      <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl shadow-black/10 dark:shadow-black/40 border border-gray-100 dark:border-gray-800 transition-all duration-200 origin-top z-50 ${
        open ? 'opacity-100 visible scale-100 translate-y-0' : 'opacity-0 invisible scale-95 -translate-y-2'
      } ${wide ? 'w-[540px]' : 'w-[300px]'}`}>
        {/* Arrow */}
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-gray-900 border-l border-t border-gray-100 dark:border-gray-800 rotate-45 rounded-tl" />
        <div className="relative p-2">
          {children}
        </div>
      </div>
    </div>
  );
};

// ─── Dropdown menu item ──────────────────────────────────────────────
const MenuItem = ({ href, icon, label, desc, gradient, external, onClick }) => {
  const Tag = external ? 'a' : 'a';
  return (
    <Tag
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      onClick={onClick}
      className="flex items-start gap-3.5 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group"
    >
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-sm group-hover:scale-105 transition-transform duration-200`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">{label}</span>
          {external && (
            <ArrowRight className="w-3 h-3 text-gray-300 dark:text-gray-600 -rotate-45 group-hover:text-orange-400 transition-colors" />
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">{desc}</p>
      </div>
    </Tag>
  );
};

// ─── Main Navigation ─────────────────────────────────────────────────
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null); // 'producto' | 'soluciones' | null

  useEffect(() => {
    setIsMounted(true);

    const getCookie = (name) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };

    const checkAuth = () => {
      if (typeof window !== 'undefined') {
        const accessToken = getCookie('token') || localStorage.getItem('token');
        setIsLoggedIn(!!accessToken);
      }
    };

    checkAuth();

    const handleScroll = () => setIsScrolled(window.scrollY > 20);

    window.addEventListener('storage', checkAuth);
    window.addEventListener('focus', checkAuth);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('focus', checkAuth);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  const dashboardUrl = brandConfig.dashboardUrl;

  return (
    <>
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl shadow-lg shadow-black/[0.04] dark:shadow-black/30'
          : 'bg-white dark:bg-gray-950'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[72px]">

            {/* ── Logo ─────────────────────────────────────── */}
            <a href="/" className="flex items-center gap-3 group flex-shrink-0">
              <div className="relative w-10 h-10 rounded-xl overflow-hidden shadow-md group-hover:shadow-lg transition-shadow duration-300 flex-shrink-0">
                <img
                  src="/images/android-chrome-192x192.png"
                  alt={brandConfig.getLogoAlt()}
                  width={40}
                  height={40}
                  className="rounded-xl group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <div className="hidden sm:block">
                <div className="text-xl font-extrabold tracking-tight leading-none text-gray-900 dark:text-white">
                  <span style={{ color: brandConfig.primaryColor }}>{brandConfig.namePrefix}</span>
                  <span>{brandConfig.nameSuffix}</span>
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 mt-0.5">{brandConfig.slogan}</p>
              </div>
            </a>

            {/* ── Desktop Nav ──────────────────────────────── */}
            <div className="hidden lg:flex items-center gap-0.5">

              {/* Producto (mega menu) */}
              <Dropdown label="Producto" wide>
                <div className="grid grid-cols-2 gap-1">
                  {PRODUCT_ITEMS.map((section) => (
                    <div key={section.section}>
                      <div className="px-3 pt-2 pb-1">
                        <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-gray-400 dark:text-gray-500">{section.section}</span>
                      </div>
                      {section.items.map((item) => (
                        <MenuItem key={item.href} {...item} />
                      ))}
                    </div>
                  ))}
                </div>
                {/* Bottom highlight */}
                <div className="mx-1 mt-1 p-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/10 rounded-xl border border-orange-100 dark:border-orange-900/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" style={{ color: brandConfig.primaryColor }} />
                      <span className="text-xs font-bold text-gray-700 dark:text-gray-300">Nuevo: Conversion CAD a GeoJSON</span>
                    </div>
                    <a href="/servicios-conversion" className="text-xs font-bold hover:underline" style={{ color: brandConfig.primaryColor }}>
                      Ver mas →
                    </a>
                  </div>
                </div>
              </Dropdown>

              {/* Soluciones */}
              <Dropdown label="Soluciones">
                {SOLUTIONS_ITEMS.map((item) => (
                  <MenuItem key={item.href} {...item} />
                ))}
                <div className="mx-1 mt-1 border-t border-gray-100 dark:border-gray-800 pt-2">
                  <a href="/como-funciona" className="flex items-center gap-2 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors group">
                    <Globe className="w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">Vision general</span>
                    <ArrowRight className="w-3 h-3 ml-auto text-gray-300 group-hover:text-orange-400 group-hover:translate-x-0.5 transition-all" />
                  </a>
                </div>
              </Dropdown>

              {/* Direct links */}
              <a href="/planes" className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-all duration-200">
                Planes
              </a>

              <a href="/nosotros" className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-all duration-200">
                Nosotros
              </a>

              <a href="/contacto" className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-all duration-200">
                Contacto
              </a>
            </div>

            {/* ── Desktop Actions ──────────────────────────── */}
            <div className="hidden lg:flex items-center gap-2.5 flex-shrink-0">
              <a
                href={brandConfig.projectDemoUrl}
                className="flex items-center gap-2 text-sm font-semibold text-gray-600 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-orange-200 dark:hover:border-orange-800 hover:bg-orange-50 dark:hover:bg-orange-950/20 hover:text-orange-700 dark:hover:text-orange-400 transition-all duration-300"
              >
                <Eye className="w-4 h-4" />
                Demo
              </a>
              {!isMounted ? null : isLoggedIn ? (
                <a
                  href={`${dashboardUrl}/dashboard`}
                  className="flex items-center gap-2 text-sm text-white px-5 py-2 rounded-xl font-bold transition-all duration-300 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/35 hover:scale-[1.03] active:scale-[0.98]"
                  style={{ background: brandConfig.getPrimaryGradient() }}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </a>
              ) : (
                <a
                  href={`${dashboardUrl}/iniciar-sesion`}
                  className="flex items-center gap-2 text-sm text-white px-5 py-2 rounded-xl font-bold transition-all duration-300 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/35 hover:scale-[1.03] active:scale-[0.98]"
                  style={{ background: brandConfig.getPrimaryGradient() }}
                >
                  <Play className="w-4 h-4" />
                  Comenzar Gratis
                </a>
              )}
            </div>

            {/* ── Mobile hamburger ─────────────────────────── */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label={isMenuOpen ? 'Cerrar menu' : 'Abrir menu'}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* ── Mobile Drawer (slide-in from right) ─────────────── */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMenuOpen(false)}
      />

      {/* Panel */}
      <div className={`fixed top-0 right-0 z-[70] h-full w-[85vw] max-w-[380px] bg-white dark:bg-gray-950 shadow-2xl transition-transform duration-300 ease-out lg:hidden flex flex-col ${
        isMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 h-[72px] border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <a href="/" className="flex items-center gap-2.5" onClick={() => setIsMenuOpen(false)}>
            <img src="/images/android-chrome-192x192.png" alt={brandConfig.getLogoAlt()} width={36} height={36} className="rounded-lg" />
            <span className="text-lg font-extrabold text-gray-900 dark:text-white tracking-tight">
              <span style={{ color: brandConfig.primaryColor }}>{brandConfig.namePrefix}</span>{brandConfig.nameSuffix}
            </span>
          </a>
          <button onClick={() => setIsMenuOpen(false)} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-500 dark:text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer body */}
        <div className="flex-1 overflow-y-auto py-4 px-3 no-scrollbar">
          {/* Producto accordion */}
          <button
            onClick={() => setMobileExpanded(mobileExpanded === 'producto' ? null : 'producto')}
            className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-gray-800 dark:text-white rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
          >
            <span className="flex items-center gap-3">
              <BarChart3 className="w-4 h-4 opacity-40" />
              Producto
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${mobileExpanded === 'producto' ? 'rotate-180' : ''}`} />
          </button>
          {mobileExpanded === 'producto' && (
            <div className="ml-2 pl-4 border-l-2 border-gray-100 dark:border-gray-800 space-y-0.5 mb-1">
              {PRODUCT_ITEMS.flatMap(s => s.items).map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                >
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white flex-shrink-0`}>
                    {item.icon}
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-800 dark:text-white block">{item.label}</span>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">{item.desc}</span>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Soluciones accordion */}
          <button
            onClick={() => setMobileExpanded(mobileExpanded === 'soluciones' ? null : 'soluciones')}
            className="flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-gray-800 dark:text-white rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
          >
            <span className="flex items-center gap-3">
              <Users className="w-4 h-4 opacity-40" />
              Soluciones
            </span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${mobileExpanded === 'soluciones' ? 'rotate-180' : ''}`} />
          </button>
          {mobileExpanded === 'soluciones' && (
            <div className="ml-2 pl-4 border-l-2 border-gray-100 dark:border-gray-800 space-y-0.5 mb-1">
              {SOLUTIONS_ITEMS.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors"
                >
                  <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white flex-shrink-0`}>
                    {item.icon}
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-800 dark:text-white block">{item.label}</span>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500">{item.desc}</span>
                  </div>
                </a>
              ))}
              <a href="/como-funciona" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-400 hover:text-orange-500 transition-colors">
                <Globe className="w-3.5 h-3.5" /> Vision general →
              </a>
            </div>
          )}

          {/* Direct links */}
          <a href="/planes" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 dark:text-white rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
            <CreditCard className="w-4 h-4 opacity-40" />
            Planes
          </a>
          <a href="/nosotros" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 dark:text-white rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
            <Users className="w-4 h-4 opacity-40" />
            Nosotros
          </a>
          <a href="/contacto" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-gray-800 dark:text-white rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
            <Mail className="w-4 h-4 opacity-40" />
            Contacto
          </a>

          {/* WhatsApp quick link */}
          <div className="mt-4 mx-1">
            <a
              href={`https://wa.me/${brandConfig.whatsappSupport.replace('+', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400 rounded-xl border border-emerald-100 dark:border-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors"
            >
              <Phone className="w-4 h-4" />
              <div>
                <span className="text-sm font-bold block">WhatsApp</span>
                <span className="text-[11px] text-emerald-600 dark:text-emerald-500">{brandConfig.whatsappSupport}</span>
              </div>
            </a>
          </div>
        </div>

        {/* Drawer footer - CTAs */}
        <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-800 p-4 space-y-2.5 bg-gray-50 dark:bg-gray-900/50">
          <a
            href={brandConfig.projectDemoUrl}
            onClick={() => setIsMenuOpen(false)}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-200 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-white dark:hover:bg-gray-800 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Ver Demo
          </a>
          {!isMounted ? null : isLoggedIn ? (
            <a
              href={`${dashboardUrl}/dashboard`}
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm text-white rounded-xl font-bold shadow-lg shadow-orange-500/20"
              style={{ background: brandConfig.getPrimaryGradient() }}
            >
              <LayoutDashboard className="w-4 h-4" />
              Ir al Dashboard
            </a>
          ) : (
            <a
              href={`${dashboardUrl}/registro`}
              onClick={() => setIsMenuOpen(false)}
              className="flex items-center justify-center gap-2 w-full px-4 py-3 text-sm text-white rounded-xl font-bold shadow-lg shadow-orange-500/20"
              style={{ background: brandConfig.getPrimaryGradient() }}
            >
              <Play className="w-4 h-4" />
              Comenzar Gratis
            </a>
          )}
        </div>
      </div>
    </>
  );
};

export default Navigation;
